/**
 * Application Color Configuration
 *
 * Dynamic color system that reads from tenant configuration. Provides full
 * color palettes (50-950 shades) for PrimeVue theming.
 */

import { generateColorPalette, normalizeHex, isValidHex, type ColorPalette, type ShadeKey } from './colorUtils'

const DEFAULT_PRIMARY_COLOR = '#e6b800'
const DEFAULT_SECONDARY_COLOR = '#181818'

const defaultPalettes: { primary: ColorPalette; secondary: ColorPalette } = {
  primary: {
    '50': '#fefce8',
    '100': '#fef9c3',
    '200': '#fef08a',
    '300': '#fde047',
    '400': '#facc15',
    '500': '#e6b800',
    '600': '#ca9f00',
    '700': '#a17f00',
    '800': '#7d6200',
    '900': '#5c4800',
    '950': '#3d2f00'
  },
  secondary: {
    '50': '#f5f5f5',
    '100': '#e0e0e0',
    '200': '#bdbdbd',
    '300': '#9e9e9e',
    '400': '#757575',
    '500': '#181818',
    '600': '#141414',
    '700': '#101010',
    '800': '#0d0d0d',
    '900': '#0a0a0a',
    '950': '#000000'
  }
}

export type SemanticColorName = 'success' | 'warning' | 'danger' | 'info'

const semanticColors: Record<SemanticColorName, { '500': string }> = {
  success: { '500': '#22c55e' },
  warning: { '500': '#f59e0b' },
  danger: { '500': '#ef4444' },
  info: { '500': '#3b82f6' }
}

export interface TenantThemeLike {
  primaryColor?: string | null
  secondaryColor?: string | null
}

export interface TenantConfigLike {
  getTheme(): TenantThemeLike
}

export interface AppColors {
  primary: ColorPalette
  secondary: ColorPalette
  success: { '500': string }
  warning: { '500': string }
  danger: { '500': string }
  info: { '500': string }
}

export function generateColors(tenantConfig: TenantConfigLike | null = null): AppColors {
  let primaryColor = DEFAULT_PRIMARY_COLOR
  let secondaryColor = DEFAULT_SECONDARY_COLOR

  if (tenantConfig && typeof tenantConfig.getTheme === 'function') {
    const theme = tenantConfig.getTheme()

    if (theme.primaryColor && isValidHex(theme.primaryColor)) {
      primaryColor = normalizeHex(theme.primaryColor)
    }

    if (theme.secondaryColor && isValidHex(theme.secondaryColor)) {
      secondaryColor = normalizeHex(theme.secondaryColor)
    }
  }

  const primaryPalette = generateColorPalette(primaryColor)
  const secondaryPalette = generateColorPalette(secondaryColor)

  return {
    primary: primaryPalette,
    secondary: secondaryPalette,
    ...semanticColors
  }
}

export const colors: AppColors = {
  ...defaultPalettes,
  ...semanticColors
}

export function getColor(color: keyof AppColors, shade: ShadeKey | number = 500): string {
  const shadeKey = String(shade) as ShadeKey
  const palette = colors[color] as Partial<ColorPalette>
  return palette?.[shadeKey] || colors.primary['500']
}

export const PRIMARY_COLOR = DEFAULT_PRIMARY_COLOR
export const SECONDARY_COLOR = DEFAULT_SECONDARY_COLOR

export { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR }
